/**
 * FeedbackBar — хорошие / нейтральные / плохие отзывы: три числа и полоска.
 * Используется на главной, в карточке машины и на странице отзывов.
 */

import type { FeedbackTotals, FeedbackRating } from '../api/client';

export const RATING_META: Record<FeedbackRating, { icon: string; label: string; color: string }> = {
  good: { icon: '👍', label: 'Good', color: '#16a34a' },
  ok: { icon: '😐', label: 'Neutral', color: '#a3a3a3' },
  bad: { icon: '👎', label: 'Bad', color: '#dc2626' },
};

/** Подписи причин с экрана FeedbackReasonScreen (киоск шлёт ключи). */
export const REASON_LABEL: Record<string, string> = {
  payment_unclear: 'Payment was unclear',
  too_slow: 'Too slow',
  upload_failed: 'File upload failed',
  price_unclear: 'Price was unclear',
  bad_print: 'Bad print quality',
};

export function reasonLabel(reason: string | null): string {
  if (!reason) return 'No reason given';
  return REASON_LABEL[reason] ?? reason;
}

const ORDER: FeedbackRating[] = ['good', 'ok', 'bad'];

/** compact — одна строка «👍 12 · 😐 2 · 👎 1» без полоски (для карточки на главной). */
export function FeedbackBar({ totals, compact }: { totals: FeedbackTotals; compact?: boolean }) {
  if (compact) {
    return (
      <span style={{ fontSize: '0.78rem', whiteSpace: 'nowrap' }} title="Customer reviews, last 30 days">
        {ORDER.map((r, i) => (
          <span key={r} style={{ color: r === 'bad' && totals.bad > 0 ? RATING_META.bad.color : undefined }}>
            {i > 0 && <span className="text-muted"> · </span>}
            {RATING_META[r].icon} {totals[r]}
          </span>
        ))}
      </span>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', marginBottom: 8 }}>
        {ORDER.map((r) => (
          <div key={r}>
            <div className="text-muted" style={{ fontSize: '0.75rem' }}>{RATING_META[r].icon} {RATING_META[r].label}</div>
            <div style={{ fontSize: '1.35rem', fontWeight: 700, color: r === 'bad' && totals.bad > 0 ? RATING_META.bad.color : undefined }}>
              {totals[r]}
              <span className="text-muted" style={{ fontSize: '0.8rem', fontWeight: 400, marginLeft: 4 }}>
                {pct(totals[r], totals.total)}
              </span>
            </div>
          </div>
        ))}
      </div>
      {totals.total > 0 && (
        <div style={{ display: 'flex', height: 8, borderRadius: 4, overflow: 'hidden', gap: 2 }}>
          {ORDER.filter((r) => totals[r] > 0).map((r) => (
            <div key={r} style={{ flex: totals[r], background: RATING_META[r].color }} title={`${RATING_META[r].label}: ${totals[r]}`} />
          ))}
        </div>
      )}
    </div>
  );
}

function pct(n: number, total: number): string {
  return total > 0 ? `${Math.round((n / total) * 100)}%` : '';
}

export function RatingBadge({ rating }: { rating: FeedbackRating }) {
  const m = RATING_META[rating];
  return (
    <span style={{
      fontSize: '0.74rem', fontWeight: 600, padding: '2px 8px', borderRadius: 4, whiteSpace: 'nowrap',
      background: rating === 'bad' ? 'rgba(239,68,68,0.15)' : rating === 'good' ? 'rgba(22,163,74,0.15)' : 'rgba(100,116,139,0.15)',
      color: rating === 'bad' ? '#b91c1c' : rating === 'good' ? '#15803d' : '#475569',
    }}>
      {m.icon} {m.label}
    </span>
  );
}
